import React, { useMemo, useState } from 'react'
import { Dropdown, Input, MenuProps, Tabs } from 'antd'
import { DotsThree, Funnel, MagnifyingGlass, UserCircle } from '@phosphor-icons/react'
import CustomModal from 'components/global/CustomModal'
import CustomAvatar from 'components/global/CustomAvatar'
import Spinner from 'components/Spinner'
import { manageTeamStore } from 'state/league/manageTeam'
import { leagueEventStore } from 'state/league/leagueEvent'
import { useGetLeaguePlayers } from 'api/league/LeagueManage/leagueManage'
import { useGetLeagueDivisions } from 'api/league/LeagueCreation/leagueDivision'
import { LeaguePlayerParams, LeaguePlayerParticipant } from 'types/league/leagueManage'
import ManagePlayersFilter from './ManagePlayersFilter'
import PlayerProfileModal from './PlayerProfileModal'
import moment from 'moment'

const { TabPane } = Tabs

export type ParticipantType = 'kid' | 'guardian' | 'authorizedAdult'

const calcAge = (dob?: string) =>
  dob ? `${moment().diff(moment(dob), 'years')} yr` : '—'

// ── Sorting helper ────────────────────────────────────────────────────────────

function sortParticipants(
  list: LeaguePlayerParticipant[],
  sortBy?: LeaguePlayerParams['sortBy'],
  sortType?: 'asc' | 'des',
): LeaguePlayerParticipant[] {
  if (!sortBy) return list
  const dir = sortType === 'des' ? -1 : 1
  return [...list].sort((a, b) => {
    if (sortBy === 'firstName') return dir * (a.firstName || '').localeCompare(b.firstName || '')
    if (sortBy === 'lastName') return dir * (a.lastName || '').localeCompare(b.lastName || '')
    if (sortBy === 'age') {
      const ageA = a.dob ? moment().diff(moment(a.dob), 'years') : 0
      const ageB = b.dob ? moment().diff(moment(b.dob), 'years') : 0
      return dir * (ageA - ageB)
    }
    return 0
  })
}

// ── Player row ────────────────────────────────────────────────────────────────

const PlayerRow: React.FC<{
  participant: LeaguePlayerParticipant
  participantType: ParticipantType
  onViewProfile: (p: LeaguePlayerParticipant, t: ParticipantType) => void
}> = ({ participant, participantType, onViewProfile }) => {
  const fullName = `${participant.firstName || ''} ${participant.lastName || ''}`.trim() || '—'

  const menuItems: MenuProps['items'] = [
    {
      key: 'view',
      label: (
        <div className="flex align-center gap-10px" onClick={() => onViewProfile(participant, participantType)}>
          <UserCircle size={18} color="#000" />
          <span>View Profile</span>
        </div>
      ),
    },
  ]

  return (
    <div className="flex align-center justify-between px-15px py-10px border-1px radius-8">
      <div className="flex align-center gap-10px">
        <CustomAvatar size={36} src={participant.photo?.thumb} name={fullName} />
        <div>
          <p className="text-14 w-500">{fullName}</p>
          <p className="text-12 text-gray" style={{ textTransform: 'capitalize' }}>
            {participant.gender || '—'} · {calcAge(participant.dob)}
          </p>
        </div>
      </div>
      <Dropdown
        trigger={['click']}
        menu={{ items: menuItems }}
        placement="bottomRight"
        arrow={false}
        overlayStyle={{ width: 160 }}
      >
        <DotsThree size={20} color="#000" style={{ cursor: 'pointer' }} />
      </Dropdown>
    </div>
  )
}

// ── Player list ───────────────────────────────────────────────────────────────

const PlayerList: React.FC<{
  participants: LeaguePlayerParticipant[]
  participantType: ParticipantType
  search: string
  onViewProfile: (p: LeaguePlayerParticipant, t: ParticipantType) => void
}> = ({ participants, participantType, search, onViewProfile }) => {
  const filtered = search
    ? participants.filter(p =>
        `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase().includes(search.toLowerCase()),
      )
    : participants

  if (!filtered.length)
    return <p className="text-gray text-14 mt-10px">No participants found</p>

  return (
    <div className="flex flex-column gap-10px mt-10px">
      {filtered.map(p => (
        <PlayerRow
          key={p._id}
          participant={p}
          participantType={participantType}
          onViewProfile={onViewProfile}
        />
      ))}
    </div>
  )
}

// ── Main modal ────────────────────────────────────────────────────────────────

const ManagePlayersModal: React.FC = () => {
  const [search, setSearch] = useState('')
  const [filterOpen, setFilterOpen] = useState(false)
  const [appliedParams, setAppliedParams] = useState<LeaguePlayerParams>({})
  const [selectedPlayer, setSelectedPlayer] = useState<{
    participant: LeaguePlayerParticipant
    type: ParticipantType
  } | null>(null)

  const { managePlayersModal, setManagePlayersModal, leagueTeams } = manageTeamStore(
    state => ({
      managePlayersModal: state.managePlayersModal,
      setManagePlayersModal: state.setManagePlayersModal,
      leagueTeams: state.leagueTeams,
    }),
  )

  const { leagueData } = leagueEventStore(store => ({ leagueData: store.leagueData }))
  const leagueId: string = (leagueData as any)?._id || ''

  const { data, isLoading, isFetching } = useGetLeaguePlayers(leagueId, appliedParams)
  const { data: divisions, refetch: fetchDivisions } = useGetLeagueDivisions(leagueId)

  const loading = isLoading || isFetching
  const rawKids = data?.participants?.kids || []
  const rawGuardians = data?.participants?.guardians || []
  const rawAuthorizedAdults = data?.participants?.authorizedAdults || []

  const kids = useMemo(
    () => sortParticipants(rawKids, appliedParams.sortBy, appliedParams.sortType),
    [rawKids, appliedParams.sortBy, appliedParams.sortType],
  )
  const guardians = useMemo(
    () => sortParticipants(rawGuardians, appliedParams.sortBy, appliedParams.sortType),
    [rawGuardians, appliedParams.sortBy, appliedParams.sortType],
  )
  const authorizedAdults = useMemo(
    () => sortParticipants(rawAuthorizedAdults, appliedParams.sortBy, appliedParams.sortType),
    [rawAuthorizedAdults, appliedParams.sortBy, appliedParams.sortType],
  )

  const hasFilters = Object.keys(appliedParams).length > 0

  const handleOpenFilter = () => {
    fetchDivisions()
    setFilterOpen(true)
  }

  const close = () => {
    setManagePlayersModal(false)
    setSearch('')
    setAppliedParams({})
  }

  return (
    <>
      <CustomModal
        openModal={managePlayersModal}
        isCloseModalCustom
        closeModal={close}
        title="Manage players"
        footer={false}
      >
        {/* Search + Filter */}
        <div className="flex gap-10px mb-15px">
          <Input
            prefix={<MagnifyingGlass size={16} color="#908F98" />}
            placeholder="Search by name"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="br-12"
            style={{ flex: 1 }}
          />
          <button
            onClick={handleOpenFilter}
            style={{
              border: hasFilters ? '2px solid #E69741' : '1px solid #EBEDF1',
              background: hasFilters ? '#FFF8F0' : '#fff',
              borderRadius: 12,
              padding: '0 14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Funnel size={18} color={hasFilters ? '#E69741' : '#908F98'} />
            {hasFilters && (
              <span style={{ fontSize: 12, color: '#E69741', fontWeight: 600 }}>On</span>
            )}
          </button>
        </div>

        {loading ? (
          <div className="py-40px flex justify-center align-center">
            <Spinner />
          </div>
        ) : (
          <Tabs defaultActiveKey="kids">
            <TabPane tab={`Kids (${kids.length})`} key="kids">
              <PlayerList
                participants={kids}
                participantType="kid"
                search={search}
                onViewProfile={(p, t) => setSelectedPlayer({ participant: p, type: t })}
              />
            </TabPane>
            <TabPane tab={`Guardians (${guardians.length})`} key="guardians">
              <PlayerList
                participants={guardians}
                participantType="guardian"
                search={search}
                onViewProfile={(p, t) => setSelectedPlayer({ participant: p, type: t })}
              />
            </TabPane>
            <TabPane tab={`Authorized Adults (${authorizedAdults.length})`} key="authorizedAdults">
              <PlayerList
                participants={authorizedAdults}
                participantType="authorizedAdult"
                search={search}
                onViewProfile={(p, t) => setSelectedPlayer({ participant: p, type: t })}
              />
            </TabPane>
          </Tabs>
        )}
      </CustomModal>

      {filterOpen && (
        <ManagePlayersFilter
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          initialParams={appliedParams}
          teams={leagueTeams || []}
          divisions={divisions || []}
          onApply={params => setAppliedParams(params)}
        />
      )}

      {selectedPlayer && (
        <PlayerProfileModal
          player={selectedPlayer.participant}
          participantType={selectedPlayer.type}
          onClose={() => setSelectedPlayer(null)}
        />
      )}
    </>
  )
}

export default ManagePlayersModal
